/* eslint-disable */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Sparkles, X, ChevronLeft, ChevronRight, Check, RotateCcw, Layers } from 'lucide-react';
import adminServices from '../../../services/adminServices';

// Floating flashcards button for the assessment details page.
//
//  - Has attempts  → the deck is built from the questions missed on the latest
//                    attempt (same job the mobile app drives): read the deck, and if
//                    there is none, run generation to completion, then show it.
//  - No attempts   → asks which topic to study and generates a standalone deck.
//
// The popup grows out of the button and shrinks back into it (transform-origin is the
// button's corner), so it always reads as the same object.

const TERMINAL = ['COMPLETED', 'SKIPPED', 'FAILED'];
const MAX_GENERATE_CALLS = 8;
const DURATION = 380;

export default function FlashcardFab({ assessmentId, assessmentTitle, attemptsMade = 0, attemptIndex }) {
  const theme = useTheme();
  const brand = theme?.palette?.primary?.main || '#2065D1';

  const hasAttempts = Number(attemptsMade) > 0;
  // attempt_count is 0-based at submit time, so the latest attempt is count - 1.
  // A page that shows one chosen attempt can pass it as attemptIndex.
  const latestAttempt = attemptIndex !== undefined && attemptIndex >= 0 && attemptIndex < attemptsMade
    ? Number(attemptIndex)
    : Math.max(0, Number(attemptsMade) - 1);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('attempt'); // 'attempt' | 'topic'
  const [phase, setPhase] = useState('idle'); // idle | loading | preparing | ready | empty | askTopic | error
  const [cards, setCards] = useState([]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [progress, setProgress] = useState(0);
  const [topic, setTopic] = useState('');
  const [error, setError] = useState('');
  const startedFor = useRef(null);

  const resetDeck = () => { setIndex(0); setFlipped(false); };

  // Reads the deck; generates when there is none.
  const loadAttemptDeck = useCallback(async () => {
    setMode('attempt');
    setError('');
    setPhase('loading');
    const deck = await adminServices.getAttemptFlashcards({ assessmentId, attemptCount: latestAttempt });
    if (deck.ok && deck.data?.cards?.length) {
      setCards(deck.data.cards); resetDeck(); setPhase('ready');
      return;
    }
    setPhase('preparing');
    setProgress(0);
    let last = null;
    for (let i = 0; i < MAX_GENERATE_CALLS; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await adminServices.generateAttemptFlashcards({ assessmentId, attemptCount: latestAttempt });
      last = res.ok ? res.data : null;
      if (!last) { setError("We couldn't prepare your flashcards. Please try again."); setPhase('error'); return; }
      if (last.progress) setProgress(last.progress);
      if (TERMINAL.includes(last.status)) break;
    }
    const after = await adminServices.getAttemptFlashcards({ assessmentId, attemptCount: latestAttempt });
    if (after.ok && after.data?.cards?.length) {
      setCards(after.data.cards); resetDeck(); setPhase('ready');
    } else if (last?.status === 'FAILED') {
      setError("We couldn't prepare your flashcards. Please try again."); setPhase('error');
    } else {
      setPhase('empty');
    }
  }, [assessmentId, latestAttempt]);

  const generateTopic = async () => {
    const t = topic.trim();
    if (!t) return;
    setMode('topic');
    setError('');
    setPhase('preparing');
    setProgress(0);
    const res = await adminServices.generateTopicFlashcards({ topics: [t], count: 6 });
    if (res.ok && res.data?.cards?.length) {
      setCards(res.data.cards); resetDeck(); setPhase('ready');
    } else {
      setError(res.data?.message || "We couldn't generate flashcards right now. Please try again.");
      setPhase('error');
    }
  };

  // First open per assessment decides what to show.
  useEffect(() => {
    if (!open || startedFor.current === `${assessmentId}:${latestAttempt}:${hasAttempts}`) return;
    startedFor.current = `${assessmentId}:${latestAttempt}:${hasAttempts}`;
    if (hasAttempts) loadAttemptDeck();
    else setPhase('askTopic');
  }, [open, assessmentId, latestAttempt, hasAttempts]);

  // Escape closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const card = cards[index];
  const goTo = (n) => { setIndex((n + cards.length) % cards.length); setFlipped(false); };
  const answer = async (status) => {
    if (mode === 'attempt' && card?.id) adminServices.reviewAttemptFlashcard(card.id, status);
    goTo(index + 1);
  };

  const chapters = [...new Set(cards.map((c) => c.chapterName).filter(Boolean))];
  const subtitle = mode === 'topic'
    ? `Topic: ${topic.trim()}`
    : chapters.length === 1 ? `From ${chapters[0]}` : chapters.length > 1 ? `From ${chapters.length} chapters you missed` : 'Built from the questions you missed';

  const css = `
    .kss-fc-fab{position:fixed;right:24px;bottom:24px;z-index:1300;display:flex;align-items:center;gap:8px;border:0;cursor:pointer;color:#fff;
      background:${brand};border-radius:999px;padding:13px 20px;font:700 14px/1 inherit;box-shadow:0 10px 24px ${brand}66;
      transition:transform ${DURATION}ms cubic-bezier(.2,.8,.2,1),opacity 200ms ease,box-shadow 200ms ease}
    .kss-fc-fab:hover{transform:translateY(-2px);box-shadow:0 14px 28px ${brand}80}
    .kss-fc-fab.hidden{transform:scale(.6);opacity:0;pointer-events:none}
    .kss-fc-fab .pulse{animation:kssFcPulse 2.4s ease-in-out infinite}
    @keyframes kssFcPulse{0%,100%{transform:scale(1) rotate(0)}50%{transform:scale(1.18) rotate(12deg)}}
    .kss-fc-panel{position:fixed;right:24px;bottom:24px;z-index:1301;width:min(400px,calc(100vw - 32px));max-height:calc(100vh - 48px);overflow:auto;
      background:#fff;border-radius:22px;box-shadow:0 24px 60px rgba(16,24,40,.28);border:1px solid #e5e7eb;transform-origin:100% 100%;
      transform:scale(.12);opacity:0;visibility:hidden;
      transition:transform ${DURATION}ms cubic-bezier(.2,.8,.2,1),opacity ${DURATION * 0.7}ms ease,visibility 0s linear ${DURATION}ms}
    .kss-fc-panel.open{transform:scale(1);opacity:1;visibility:visible;transition-delay:0s}
    .kss-fc-flip{perspective:1200px;height:230px;cursor:pointer}
    .kss-fc-inner{position:relative;width:100%;height:100%;transition:transform 550ms;transform-style:preserve-3d}
    .kss-fc-inner.flipped{transform:rotateY(180deg)}
    .kss-fc-face{position:absolute;inset:0;backface-visibility:hidden;-webkit-backface-visibility:hidden;border-radius:18px;display:flex;flex-direction:column;
      align-items:center;justify-content:center;padding:22px;text-align:center;overflow:auto}
    .kss-fc-front{background:#fff;border:1.5px solid #e5e7eb}
    .kss-fc-back{background:${brand};color:#fff;transform:rotateY(180deg)}
    .kss-fc-btn{flex:1;display:flex;align-items:center;justify-content:center;gap:6px;border-radius:999px;padding:10px 0;font:700 13px/1 inherit;cursor:pointer}
    .kss-fc-spin{width:22px;height:22px;border-radius:50%;border:3px solid ${brand}33;border-top-color:${brand};animation:kssFcSpin .8s linear infinite}
    @keyframes kssFcSpin{to{transform:rotate(360deg)}}
    @media (prefers-reduced-motion:reduce){.kss-fc-fab,.kss-fc-panel,.kss-fc-inner{transition-duration:1ms}.kss-fc-fab .pulse,.kss-fc-spin{animation:none}}
  `;

  const circleBtn = (filled) => ({
    width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    border: filled ? 0 : '1.5px solid #d0d5dd', background: filled ? brand : '#fff', color: filled ? '#fff' : '#111827',
  });

  const askTopicView = (
    <div>
      <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: '#111827' }}>
        {hasAttempts ? 'Study a topic' : 'Which topic do you want to revise?'}
      </p>
      <p style={{ margin: '6px 0 14px', fontSize: 13, color: '#667085', lineHeight: 1.5 }}>
        {hasAttempts
          ? 'Type any topic and we will make you a fresh set of flashcards.'
          : 'You have not attempted this assessment yet, so tell us a topic and we will make flashcards for it. After your first attempt, cards will be built from the questions you miss.'}
      </p>
      <input
        autoFocus
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') generateTopic(); }}
        placeholder="e.g. Cell biology, Newton's laws…"
        maxLength={120}
        style={{ width: '100%', boxSizing: 'border-box', padding: '11px 14px', borderRadius: 12, border: '1.5px solid #d0d5dd', fontSize: 14, outline: 'none' }}
      />
      <button
        className="kss-fc-btn"
        disabled={!topic.trim()}
        onClick={generateTopic}
        style={{ width: '100%', marginTop: 12, border: 0, background: brand, color: '#fff', opacity: topic.trim() ? 1 : 0.5 }}
      >
        <Sparkles size={15} /> Create flashcards
      </button>
      {hasAttempts && (
        <button onClick={loadAttemptDeck} style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 0, color: brand, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          Back to my mistakes deck
        </button>
      )}
    </div>
  );

  let body = null;
  if (phase === 'loading' || phase === 'preparing') {
    body = (
      <div style={{ padding: '18px 4px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div className="kss-fc-spin" />
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
              {phase === 'loading' ? 'Loading your flashcards' : 'Preparing your flashcards'}
            </div>
            <div style={{ fontSize: 12, color: '#667085', marginTop: 2 }}>
              {mode === 'topic' ? 'Creating cards for your topic.' : 'Working through the questions you missed. This can take a minute.'}
            </div>
          </div>
        </div>
        {phase === 'preparing' && mode === 'attempt' && (
          <div style={{ height: 8, borderRadius: 8, background: '#eef0f3', marginTop: 16, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.max(5, Math.min(100, progress))}%`, background: brand, transition: 'width .4s' }} />
          </div>
        )}
      </div>
    );
  } else if (phase === 'askTopic') {
    body = askTopicView;
  } else if (phase === 'empty') {
    body = (
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        <p style={{ margin: 0, fontWeight: 700, fontSize: 16, color: '#111827' }}>Nothing to review</p>
        <p style={{ margin: '6px 0 14px', fontSize: 13, color: '#667085', lineHeight: 1.5 }}>
          You did not get anything wrong on your last attempt, so there is nothing to make cards from.
        </p>
        <button className="kss-fc-btn" onClick={() => setPhase('askTopic')} style={{ width: '100%', border: `1.5px solid ${brand}`, background: '#fff', color: brand }}>
          Study a topic instead
        </button>
      </div>
    );
  } else if (phase === 'error') {
    body = (
      <div style={{ textAlign: 'center', padding: '8px 0' }}>
        <p style={{ margin: '0 0 14px', fontSize: 13.5, color: '#b42318' }}>{error}</p>
        <button className="kss-fc-btn" onClick={() => (mode === 'topic' ? generateTopic() : loadAttemptDeck())} style={{ width: '100%', border: 0, background: brand, color: '#fff' }}>
          Try again
        </button>
      </div>
    );
  } else if (phase === 'ready' && card) {
    body = (
      <div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: `${brand}14`, color: brand, borderRadius: 999, padding: '5px 12px', fontSize: 11.5, fontWeight: 700, maxWidth: '100%' }}>
          <Sparkles size={12} /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</span>
        </div>

        <div className="kss-fc-flip" style={{ marginTop: 14 }} onClick={() => setFlipped((f) => !f)}>
          <div className={`kss-fc-inner${flipped ? ' flipped' : ''}`}>
            <div className="kss-fc-face kss-fc-front">
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: brand }}>
                {card.chapterName || card.topic || 'Focus area'}
              </div>
              <div style={{ marginTop: 12, fontSize: 18, lineHeight: 1.45, fontWeight: 600, color: '#111827' }}>{card.front}</div>
              <div style={{ position: 'absolute', bottom: 12, fontSize: 11.5, color: '#98a2b3' }}>Click to reveal answer</div>
            </div>
            <div className="kss-fc-face kss-fc-back">
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', opacity: 0.7 }}>Answer</div>
              <div style={{ marginTop: 12, fontSize: 15, lineHeight: 1.55 }}>{card.back}</div>
              <div style={{ position: 'absolute', bottom: 12, fontSize: 11.5, opacity: 0.6 }}>Click to flip back</div>
            </div>
          </div>
        </div>

        {mode === 'attempt' && (
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button className="kss-fc-btn" onClick={() => answer('again')} style={{ border: '1.5px solid #d0d5dd', background: '#fff', color: '#475467' }}>
              <RotateCcw size={14} /> Review again
            </button>
            <button className="kss-fc-btn" onClick={() => answer('known')} style={{ border: 0, background: '#067647', color: '#fff' }}>
              <Check size={14} /> Got it
            </button>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 22, marginTop: 14 }}>
          <button aria-label="Previous card" style={circleBtn(false)} onClick={() => goTo(index - 1)}><ChevronLeft size={18} /></button>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#475467' }}>{index + 1} / {cards.length}</span>
          <button aria-label="Next card" style={circleBtn(true)} onClick={() => goTo(index + 1)}><ChevronRight size={18} /></button>
        </div>

        <button
          onClick={() => setPhase('askTopic')}
          style={{ display: 'block', margin: '14px auto 0', background: 'none', border: 0, color: brand, fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}
        >
          Study a different topic
        </button>
      </div>
    );
  }

  return (
    <>
      <style>{css}</style>

      <button className={`kss-fc-fab${open ? ' hidden' : ''}`} onClick={() => setOpen(true)} aria-label="Open flashcards" aria-expanded={open} tabIndex={open ? -1 : 0}>
        <Layers size={18} className="pulse" /> Flashcards
      </button>

      <div className={`kss-fc-panel${open ? ' open' : ''}`} role="dialog" aria-label="Flashcards" aria-hidden={!open}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <Layers size={18} color={brand} />
            <span style={{ fontWeight: 800, fontSize: 15, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {assessmentTitle ? `Flashcards · ${assessmentTitle}` : 'Flashcards'}
            </span>
          </div>
          <button aria-label="Close flashcards" onClick={() => setOpen(false)} style={{ border: 0, background: '#f2f4f7', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={16} />
          </button>
        </div>
        <div style={{ padding: '10px 16px 18px' }}>{body}</div>
      </div>
    </>
  );
}
